import { Spin } from 'antd'
import Color from 'color'
import {
  useFilters,
  useGetPrecedingFilters,
} from 'features/drilldown/data/DrilldownFilterContext'
import { useVersionAwareDrilldownPanelData } from 'features/drilldown/data/DrilldownPanelDataContext'
import {
  useHistogramQueries,
  UseHistogramQueryProps,
} from 'features/drilldown/rolodex/dataHelpers/drilldownDataHelpers'
import Plot from 'features/plotly'
import { useRolodexHistogram } from 'features/rolodex'
import _ from 'lodash'
import { ColorScale, PlotMouseEvent } from 'plotly.js'
import React, {
  Dispatch,
  FC,
  SetStateAction,
  useCallback,
  useMemo,
} from 'react'
import { GenericPanelData } from 'types'

const LATEST_DATA_SCHEMA_VERSION = 1

const RISK_LABELS = ['1', '2', '3', '4', '5'] as const

const PROBABILITY_CATEGORIES = {
  highly_unlikely: 'Highly Unlikely',
  unlikely: 'Unlikely',
  probable: 'Probable',
  likely: 'Likely',
  highly_likely: 'Highly Likely',
} as const

function probablilityCategoryFromLabel(
  label: string
): ProbabilityCategory | null {
  return (
    (Object.entries(PROBABILITY_CATEGORIES).find(
      ([, lab]) => lab === label
    )?.[0] as ProbabilityCategory) ?? null
  )
}

type RiskCategory = (typeof RISK_LABELS)[number]
type ProbabilityCategory = keyof typeof PROBABILITY_CATEGORIES

type RbiRec = RbiCategory & {
  count: number
}

type RbiCategory = {
  risk: RiskCategory | null
  probability: ProbabilityCategory | null
}

// Encoder/Decoder to put the 'risk' and 'probablility' records
// to and from strings, so we can do efficient 'uniq' checks.
function encodeSelected({ risk, probability }: RbiCategory): string {
  return `${risk ?? ''}:${probability ?? ''}`
}

function decodeSelected(encoded: string): RbiCategory {
  const [riskStr, probabilityStr] = encoded.split(':')
  const risk = RISK_LABELS.includes(riskStr as RiskCategory)
    ? (riskStr as RiskCategory)
    : null
  const probability = PROBABILITY_CATEGORIES[
    probabilityStr as ProbabilityCategory
  ]
    ? (probabilityStr as ProbabilityCategory)
    : null

  return { risk, probability }
}

// base z-values to produce the colors
const RISK_LEVELS: ReadonlyArray<ReadonlyArray<number>> = [
  [0, 0, 0, 0, 0, 0],
  [0, 0.2, 0.2, 0.2, 0.3, 0.4],
  [0, 0.2, 0.2, 0.3, 0.4, 0.6],
  [0, 0.2, 0.3, 0.4, 0.6, 0.8],
  [0, 0.3, 0.4, 0.6, 0.8, 1],
  [0, 0.4, 0.6, 0.8, 1, 1],
]

type RbiMatricPanelData = GenericPanelData & {
  selectedValues: ReadonlyArray<RbiCategory>
}

export const DEFAULT_RBI_MATRIX_PANEL_DATA: RbiMatricPanelData = {
  title: 'RBI Matrix',
  panelDataSchemaVersion: LATEST_DATA_SCHEMA_VERSION,
  isPinned: true,
  selectedValues: [],
}

const LBL_NO_DATA = 'No Data'

// Helper to generate the 'bubble' annotation and shape
// from an Rbi rec
function recToAnnotation(
  rec: RbiRec,
  xValues: ReadonlyArray<string>,
  yValues: ReadonlyArray<string>
): [Partial<Plotly.Annotations>, Partial<Plotly.Shape>] {
  const x = rec.risk ?? LBL_NO_DATA
  const y = rec.probability
    ? PROBABILITY_CATEGORIES[rec.probability]
    : LBL_NO_DATA
  return [
    {
      x,
      y,
      text: `${rec.count}`,
      showarrow: false,
      font: {
        color: 'blue',
        size: 18,
        family: 'Arial',
      },
      xref: 'x',
      yref: 'y',
      align: 'center',
      valign: 'middle',
    },
    {
      type: 'circle',
      xref: 'x',
      yref: 'y',
      x0: xValues.indexOf(x) - 0.3,
      x1: xValues.indexOf(x) + 0.3,
      y0: yValues.indexOf(y) - 0.3,
      y1: yValues.indexOf(y) + 0.3,
      line: {
        color: 'blue',
        width: 2,
      },
      fillcolor: 'white',
    },
  ]
}

// Helper function to generate a base color
const getColorFromZ = (z: number): string => {
  const scale = [
    { value: 0, color: '#EAEAEA' },
    { value: 0.2, color: '#AAFF00' },
    { value: 0.2, color: '#3D9970' },
    { value: 0.4, color: '#FFDC00' },
    { value: 0.6, color: '#FF851B' },
    { value: 0.8, color: '#FF4136' },
    { value: 1, color: '#85144B' },
  ]

  // Find two nearest points in the scale
  let lower = scale[0]
  let upper = scale[scale.length - 1]

  for (let i = 0; i < scale.length - 1; i++) {
    if (z >= scale[i].value && z <= scale[i + 1].value) {
      lower = scale[i]
      upper = scale[i + 1]
      break
    }
  }

  // Linear interpolation
  const ratio =
    upper.value === lower.value
      ? 0
      : (z - lower.value) / (upper.value - lower.value)

  return Color(lower.color).mix(Color(upper.color), ratio).hex()
}

// Generate the plot.
// If no selected cells, it's the standard colored RBI plot
// If there are selected cells, those cells retain their color
// while the others are faded out to indicate they're being filtered
function useRbiPlot(selectedCells: ReadonlyArray<RbiCategory>): {
  xValues: ReadonlyArray<string>
  yValues: ReadonlyArray<string>
  zIndices: ReadonlyArray<ReadonlyArray<number>>
  colorscale: ColorScale
} {
  const xValues = useMemo<ReadonlyArray<string>>(
    () => [LBL_NO_DATA, ...RISK_LABELS],
    []
  )
  const yValues = useMemo<ReadonlyArray<string>>(
    () => [LBL_NO_DATA, ...Object.values(PROBABILITY_CATEGORIES)],
    []
  )

  const { customColorscale, zIndices } = useMemo(() => {
    const colorMatrix = RISK_LEVELS.map((row, rowIndex) =>
      row.map((cellValue, colIndex) => {
        const risk = (
          RISK_LABELS.includes(xValues[colIndex] as RiskCategory)
            ? xValues[colIndex]
            : null
        ) as RiskCategory | null
        const probability = probablilityCategoryFromLabel(
          yValues[rowIndex] as string
        )
        const cellKey = encodeSelected({ risk, probability })

        const originalColor = getColorFromZ(cellValue)

        if (
          !selectedCells.length ||
          selectedCells.map(encodeSelected).includes(cellKey)
        ) {
          return originalColor // original color
        } else {
          // Fade the color by desaturating and lightening
          return Color(originalColor).desaturate(0.8).lighten(0.5).hex()
        }
      })
    )
    const flattenedColors = colorMatrix.flat()
    // Create a colorscale mapping each index to its color
    return {
      customColorscale: flattenedColors.map((color, idx) => [
        idx / (flattenedColors.length - 1),
        color,
      ]) as ColorScale,
      zIndices: colorMatrix.map((row, rowIndex) =>
        row.map((_, colIndex) => rowIndex * row.length + colIndex)
      ),
    }
  }, [selectedCells, xValues, yValues])

  return useMemo(
    () => ({
      xValues,
      yValues,
      zIndices,
      colorscale: customColorscale,
    }),
    [customColorscale, xValues, yValues, zIndices]
  )
}

const RBI_ENTRY_TYPE = 'unit-rbi'

// Fetches the RBI entries from Rolodex,
// and produces 'annotations' and 'shapes' to add
// to the plot to form the 'bubbles'
function useRbiBubbles(
  xValues: ReadonlyArray<string>,
  yValues: ReadonlyArray<string>
): {
  annotations: ReadonlyArray<Partial<Plotly.Annotations>>
  shapes: ReadonlyArray<Partial<Plotly.Shape>>
  isLoading: boolean
} {
  const filters = useGetPrecedingFilters()
  const queryProps = useMemo<UseHistogramQueryProps>(
    () => ({
      select: [
        { key: 'rbi', type: RBI_ENTRY_TYPE, group_by: true, label: 'rbi' },
        {
          field: 'id',
          type: RBI_ENTRY_TYPE,
          op: 'count',
          label: 'count',
        },
      ],
      filters,
    }),
    [filters]
  )
  const {
    queries: [query],
    isLoading: queryLoading,
  } = useHistogramQueries(queryProps)
  const { data: histoResponse, isLoading: dataLoading } =
    useRolodexHistogram(query)
  const data = useMemo(
    () =>
      (histoResponse ?? []).flat().flatMap((resp) => {
        const rbiStr = resp['rbi'] as string
        const rbi = rbiStr ? decodeSelected(rbiStr) : null
        return {
          risk: rbi?.risk ?? null,
          probability: rbi?.probability ?? null,
          count: (resp['count'] as number) ?? 0,
        }
      }),
    [histoResponse]
  )

  const [annotations, shapes] = useMemo(
    () =>
      _.unzip(data.map((rec) => recToAnnotation(rec, xValues, yValues))) as [
        Partial<Plotly.Annotations>[],
        Partial<Plotly.Shape>[],
      ],
    [data, xValues, yValues]
  )

  const isLoading = dataLoading || queryLoading

  return useMemo(
    () => ({ annotations: annotations ?? [], shapes: shapes ?? [], isLoading }),
    [annotations, shapes, isLoading]
  )
}

// Click handler - takes the Plotly click events and updates
// the selected cells in the panel data
function useRbiClickHandler(
  setPanelData: Dispatch<SetStateAction<RbiMatricPanelData>>
): (event: PlotMouseEvent) => void {
  const setSelectedCells = useCallback(
    (val: SetStateAction<ReadonlyArray<RbiCategory>>) =>
      setPanelData((prev) => {
        const newVal =
          typeof val === 'function' ? val(prev.selectedValues) : val
        // Uniq it
        const newValUniq = _.uniq(newVal.map(encodeSelected)).map(
          decodeSelected
        )
        return { ...prev, selectedValues: newValUniq }
      }),
    [setPanelData]
  )

  return useCallback(
    (event: PlotMouseEvent) => {
      if (event.points && event.points.length > 0) {
        const point = event.points[0]
        const risk = (
          RISK_LABELS.includes(point.x as RiskCategory) ? point.x : null
        ) as RiskCategory | null
        const probability = probablilityCategoryFromLabel(point.y as string)
        // Don't allow selection of the 'No Data' - we can't combine filters on that, sadly
        if (!risk || !probability) {
          return
        }
        const cellKey = encodeSelected({ risk, probability })

        setSelectedCells((prev) => {
          if (prev.map(encodeSelected).includes(cellKey)) {
            return prev.filter((sel) => encodeSelected(sel) !== cellKey)
          } else {
            return [...prev, { risk, probability }]
          }
        })
      }
    },
    [setSelectedCells]
  )
}

// Applies the Drilldown filters from the selected cells, if any
function useFilterRbi(selectedCells: ReadonlyArray<RbiCategory>) {
  useFilters(
    useMemo(
      () =>
        selectedCells.length
          ? [
              {
                type: RBI_ENTRY_TYPE,
                key: 'rbi',
                op: 'in',
                value: selectedCells
                  .map((cell) => encodeSelected(cell))
                  .filter((cell): cell is string => !!cell),
              },
            ]
          : [],
      [selectedCells]
    )
  )
}

const RawRbiMatrix: FC = () => {
  const { panelData, setPanelData } =
    useVersionAwareDrilldownPanelData<RbiMatricPanelData>()

  const { selectedValues: selectedCells } = panelData

  const { xValues, yValues, zIndices, colorscale } = useRbiPlot(selectedCells)
  const { annotations, shapes, isLoading } = useRbiBubbles(xValues, yValues)
  const handleClick = useRbiClickHandler(setPanelData)
  useFilterRbi(selectedCells)

  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'center',
        width: '100%',
        height: '100%',
      }}>
      <Spin spinning={isLoading}>
        <RbiMatrixPlot
          x={xValues}
          y={yValues}
          z={zIndices}
          colorscale={colorscale}
          clickHandler={handleClick}
          annotations={annotations}
          shapes={shapes}
        />
      </Spin>
    </div>
  )
}

export const RbiMatrix = React.memo(RawRbiMatrix)

const RbiMatrixPlot: FC<{
  x: ReadonlyArray<string>
  y: ReadonlyArray<string>
  z: ReadonlyArray<ReadonlyArray<number>>
  colorscale: ColorScale
  clickHandler: (clickEvent: PlotMouseEvent) => void
  annotations: ReadonlyArray<Partial<Plotly.Annotations>>
  shapes: ReadonlyArray<Partial<Plotly.Shape>>
}> = ({ x, y, z, colorscale, clickHandler, annotations, shapes }) => {
  return (
    <Plot
      data={[
        {
          x: [...x],
          y: [...y],
          z: [...z.map((z) => [...z])],
          type: 'heatmap',
          colorscale,
          showscale: false,
        },
      ]}
      config={{
        displayModeBar: true,
        displaylogo: false,
        responsive: true,
        scrollZoom: false,
        modeBarButtonsToRemove: [
          'zoom2d',
          'pan2d',
          'select2d',
          'lasso2d',
          'resetScale2d',
          'zoomIn2d',
          'zoomOut2d',
          'autoScale2d',
          'resetViewMapbox',
          'hoverClosestCartesian',
          'hoverCompareCartesian',
          'toImage',
        ],
      }}
      layout={{
        title: {
          yref: 'container',
        },
        xaxis: {
          ticks: '',
          type: 'category',
          color: '#FFFFFF',
          side: 'bottom',
          title: {
            text: 'Consequence',
          },
          automargin: true,
        },
        yaxis: {
          ticks: '',
          ticksuffix: ' ',
          color: '#FFFFFF',
          tickwidth: 100,
          automargin: true,
          title: {
            text: 'Likelihood',
            standoff: 10,
          },
        },
        autosize: true,
        height: 520,
        width: 600,
        plot_bgcolor: 'rgba(0, 0, 0, 0)',
        paper_bgcolor: 'rgba(0, 0, 0, 0)',
        showlegend: true,
        margin: {
          l: 40,
          t: 0,
          r: 40,
          b: 60,
        },
        annotations: [...annotations],
        shapes: [...shapes],
      }}
      style={{ height: '100%' }}
      onClick={clickHandler}
    />
  )
}
